/**
 * Threedium (Unlimited3D) Integration Status Component
 * 
 * Shows the user's Threedium connection status.
 * Keys are automatically managed via Threedium OAuth - no manual entry needed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { threediumApi } from '../services/cloudApi';
import { useAuth } from '../context/AuthContext';
import './ThreediumSettings.css';

// SVG Icons
const CubeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

export function ThreediumSettings() {
    const [keysStatus, setKeysStatus] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();

    const loadStatus = useCallback(async () => {
        try {
            const [status, info, sub] = await Promise.allSettled([
                threediumApi.getKeysStatus(),
                threediumApi.getUserInfo(),
                threediumApi.getSubscription()
            ]);
            
            if (status.status === 'fulfilled') setKeysStatus(status.value);
            if (info.status === 'fulfilled') setUserInfo(info.value);
            if (sub.status === 'fulfilled') setSubscription(sub.value);
        } catch (err) {
            console.error('Failed to load Threedium status:', err);
            setKeysStatus({ has_keys: false, is_valid: false });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    if (isLoading) {
        return (
            <div className="threedium-settings">
                <div className="card-header">
                    <h2><CubeIcon /> Threedium Integration</h2>
                </div>
                <div className="loading-state">Loading...</div>
            </div>
        );
    }

    return (
        <div className="threedium-settings">
            <div className="card-header">
                <h2><CubeIcon /> Threedium Integration</h2>
            </div>

            <p className="settings-description">
                Your Threedium account is automatically connected via OAuth.
                API keys are managed securely — no manual configuration needed.
            </p>

            {/* Connection Status */}
            <div className="keys-status">
                <div className="status-row">
                    <span className="status-label">Connection</span>
                    <span className={`status-badge ${keysStatus?.has_keys && keysStatus?.is_valid ? 'valid' : 'invalid'}`}>
                        {keysStatus?.has_keys && keysStatus?.is_valid ? (
                            <><CheckIcon /> Connected</>
                        ) : 'Not Connected'}
                    </span>
                </div>

                {/* Threedium Profile Info */}
                {userInfo && (
                    <>
                        <div className="status-row">
                            <span className="status-label">Threedium Account</span>
                            <span className="status-value">{userInfo.email}</span>
                        </div>
                        {userInfo.company && (
                            <div className="status-row">
                                <span className="status-label">Company</span>
                                <span className="status-value">{userInfo.company}</span>
                            </div>
                        )}
                        {userInfo.plan_name && (
                            <div className="status-row">
                                <span className="status-label">Plan</span>
                                <span className="status-value" style={{ textTransform: 'capitalize' }}>
                                    {userInfo.plan_name}
                                </span>
                            </div>
                        )}
                    </>
                )}

                {/* Subscription Details */}
                {subscription && (
                    <>
                        {subscription.solutions_total != null && (
                            <div className="status-row">
                                <span className="status-label">Solutions</span>
                                <span className="status-value">
                                    {subscription.solutions_used} / {subscription.solutions_total}
                                </span>
                            </div>
                        )}
                        {subscription.views != null && (
                            <div className="status-row">
                                <span className="status-label">Monthly Views</span>
                                <span className="status-value">
                                    {subscription.views?.toLocaleString()}
                                </span>
                            </div>
                        )}
                    </>
                )}

                {keysStatus?.last_validated && (
                    <div className="status-row">
                        <span className="status-label">Last Synced</span>
                        <span className="status-value">
                            {new Date(keysStatus.last_validated).toLocaleString()}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ThreediumSettings;
